import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from './components/Header';
import ReviewComponent from './components/ReviewComponent';
import { logUserEvent } from './utils/logger';

function VideoPlayer() {
  const { courseId } = useParams();
  const videoRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch the media URL from the API
    const fetchMediaUrl = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/student/courses/${courseId}/media/video`);
        if (!response.ok) {
          throw new Error('Failed to load video');
        }
        const data = await response.json();
        setMediaUrl(data.url);
        setError(null);
        logUserEvent('material_access', 'Accessed video', { course_id: courseId, material_type: 'video' });
      } catch (err) {
        console.error('Error loading video:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMediaUrl();
  }, [courseId]);

  useEffect(() => {
    // Add additional protection against downloading
    const video = videoRef.current;
    if (video) {
      // Disable right-click context menu
      video.addEventListener('contextmenu', (e) => e.preventDefault());

      // Disable drag and drop
      video.addEventListener('dragstart', (e) => e.preventDefault());

      // Disable text selection
      video.style.userSelect = 'none';
      video.style.webkitUserSelect = 'none';

      // Add additional attributes to make downloading harder
      video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
      video.setAttribute('disablePictureInPicture', 'true');
    }
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton component={RouterLink} to="/dashboard" aria-label="Back" sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" component="h1" sx={{
            flexGrow: 1,
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}>
            Course Video
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#000', minHeight: 400 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <Typography color="error">
                Error loading video: {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && mediaUrl && (
            <video
              ref={videoRef}
              width="100%"
              height="auto"
              controls
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              style={{
                maxHeight: '70vh',
                objectFit: 'contain',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              <source
                src={mediaUrl}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          This video is protected content. Downloading or sharing is not permitted.
        </Typography>

        {/* Review Component - only shown for @deepalert.ai users */}
        <ReviewComponent courseId={courseId} materialType="video" />
      </Container>
    </Box>
  );
}

export default VideoPlayer;
